/**
 * Kundali Chart Components
 * South Indian and North Indian style chart renderers
 */

import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { SIGNS, SIGN_ABBR, SIGN_ABBR_TE, PLANET_COLORS, PLANET_ABBR_TE } from '../../data/kundaliConstants';

/**
 * South Indian Kundali Chart Component
 * Signs are FIXED in positions
 */
export const SouthIndianChart = ({ chartData, title, ascendantSign, language = 'en' }) => {
  // South Indian chart - Signs are FIXED in positions
  // Layout: 4x4 grid with signs in fixed positions
  const signPositions = {
    'Pisces': { row: 0, col: 0 },
    'Aries': { row: 0, col: 1 },
    'Taurus': { row: 0, col: 2 },
    'Gemini': { row: 0, col: 3 },
    'Aquarius': { row: 1, col: 0 },
    'Cancer': { row: 1, col: 3 },
    'Capricorn': { row: 2, col: 0 },
    'Leo': { row: 2, col: 3 },
    'Sagittarius': { row: 3, col: 0 },
    'Scorpio': { row: 3, col: 1 },
    'Libra': { row: 3, col: 2 },
    'Virgo': { row: 3, col: 3 },
  };

  const signAbbr = language === 'te' ? SIGN_ABBR_TE : SIGN_ABBR;
  const planetAbbr = language === 'te' ? PLANET_ABBR_TE : null;
  const ascText = language === 'te' ? 'ల' : 'Asc';

  const cellSize = 70;
  const padding = 10;

  // Get planets for each sign from chartData
  const getPlanetsInSign = (signName) => {
    const ascIndex = SIGNS.indexOf(ascendantSign);
    const signIndex = SIGNS.indexOf(signName);
    const house = ((signIndex - ascIndex + 12) % 12) + 1;
    return chartData[house] || [];
  };

  return (
    <svg viewBox="0 0 300 300" className="kundali-chart-svg">
      {/* Draw grid cells for outer ring */}
      {Object.entries(signPositions).map(([sign, pos]) => {
        const x = padding + pos.col * cellSize;
        const y = padding + pos.row * cellSize;
        const planets = getPlanetsInSign(sign);
        const isAscendant = sign === ascendantSign;

        return (
          <g key={sign}>
            {/* Cell border */}
            <rect
              x={x}
              y={y}
              width={cellSize}
              height={cellSize}
              fill={isAscendant ? 'rgba(255, 215, 0, 0.15)' : 'transparent'}
              stroke="var(--accent-gold)"
              strokeWidth="1.5"
            />

            {/* Sign abbreviation */}
            <text
              x={x + 5}
              y={y + 12}
              fill={isAscendant ? 'var(--accent-gold)' : 'var(--text-secondary)'}
              fontSize="9"
              fontWeight={isAscendant ? 'bold' : 'normal'}
            >
              {signAbbr[sign]}
              {isAscendant && ` (${ascText})`}
            </text>

            {/* Planets in this sign */}
            {planets.map((planet, idx) => (
              <text
                key={`${sign}-${planet.abbr}`}
                x={x + 10 + (idx % 2) * 30}
                y={y + 30 + Math.floor(idx / 2) * 18}
                fill={PLANET_COLORS[planet.abbr] || 'var(--text-primary)'}
                fontSize="13"
                fontWeight="bold"
              >
                {planetAbbr ? planetAbbr[planet.abbr] : planet.abbr}
                {planet.retrograde && <tspan fill="#FF0000" fontSize="10">℞</tspan>}
              </text>
            ))}
          </g>
        );
      })}

      {/* Center area - can add additional info */}
      <rect
        x={padding + cellSize}
        y={padding + cellSize}
        width={cellSize * 2}
        height={cellSize * 2}
        fill="transparent"
        stroke="var(--accent-gold)"
        strokeWidth="1"
      />
      <text
        x={150}
        y={140}
        textAnchor="middle"
        fill="var(--accent-gold)"
        fontSize="11"
        fontWeight="bold"
      >
        {title.includes('Navamsa') || title.includes('నవాంశ') ? 'D9' : 'D1'}
      </text>
      <text
        x={150}
        y={158}
        textAnchor="middle"
        fill="var(--text-secondary)"
        fontSize="9"
      >
        {title.includes('Navamsa') || title.includes('నవాంశ') ? (language === 'te' ? 'నవాంశ' : 'Navamsa') : (language === 'te' ? 'రాశి' : 'Rashi')}
      </text>
    </svg>
  );
};

/**
 * North Indian Kundali Chart Component (Diamond style)
 * Houses are FIXED in positions, signs rotate based on Lagna
 */
export const NorthIndianChart = ({ chartData, title, ascendantSign, language = 'en' }) => {
  const signAbbr = language === 'te' ? SIGN_ABBR_TE : SIGN_ABBR;
  const planetAbbr = language === 'te' ? PLANET_ABBR_TE : null;
  const ascText = language === 'te' ? 'ల' : 'Asc';

  const size = 300;
  const center = size / 2;
  const outerSize = 130;
  const innerSize = 65;

  // Get sign for each house based on ascendant
  const getSignForHouse = (houseNum) => {
    const ascIndex = SIGNS.indexOf(ascendantSign);
    return SIGNS[(ascIndex + houseNum - 1) % 12];
  };

  // More accurate house polygons for North Indian diamond chart
  const housePolygons = [
    // House 1 - Top center (Lagna diamond)
    { house: 1, points: `${center},10 ${center - outerSize},${center} ${center},${center} ${center + outerSize},${center}`, textX: center, textY: 65, planetsX: center - 20, planetsY: 90 },
    // House 12 - Top left triangle
    { house: 12, points: `${center},10 ${center - outerSize},${center} ${center - innerSize},${center - innerSize}`, textX: center - 75, textY: 90, planetsX: center - 85, planetsY: 115 },
    // House 2 - Top right triangle
    { house: 2, points: `${center},10 ${center + innerSize},${center - innerSize} ${center + outerSize},${center}`, textX: center + 75, textY: 90, planetsX: center + 60, planetsY: 115 },
    // House 11 - Upper left
    { house: 11, points: `10,10 ${center},10 ${center - innerSize},${center - innerSize} 10,${center}`, textX: 50, textY: center - 50, planetsX: 35, planetsY: center - 25 },
    // House 3 - Upper right
    { house: 3, points: `${center},10 ${size - 10},10 ${size - 10},${center} ${center + innerSize},${center - innerSize}`, textX: size - 50, textY: center - 50, planetsX: size - 65, planetsY: center - 25 },
    // House 10 - Middle left
    { house: 10, points: `10,${center} ${center - innerSize},${center - innerSize} ${center - innerSize},${center + innerSize}`, textX: 50, textY: center, planetsX: 35, planetsY: center + 25 },
    // House 4 - Middle right
    { house: 4, points: `${size - 10},${center} ${center + innerSize},${center - innerSize} ${center + innerSize},${center + innerSize}`, textX: size - 50, textY: center, planetsX: size - 65, planetsY: center + 25 },
    // House 9 - Lower left
    { house: 9, points: `10,${center} ${center - innerSize},${center + innerSize} 10,${size - 10}`, textX: 50, textY: center + 50, planetsX: 35, planetsY: center + 75 },
    // House 5 - Lower right
    { house: 5, points: `${size - 10},${center} ${center + innerSize},${center + innerSize} ${size - 10},${size - 10}`, textX: size - 50, textY: center + 50, planetsX: size - 65, planetsY: center + 75 },
    // House 8 - Bottom left triangle
    { house: 8, points: `${center},${size - 10} ${center - outerSize},${center} ${center - innerSize},${center + innerSize}`, textX: center - 75, textY: size - 85, planetsX: center - 85, planetsY: size - 55 },
    // House 6 - Bottom right triangle
    { house: 6, points: `${center},${size - 10} ${center + outerSize},${center} ${center + innerSize},${center + innerSize}`, textX: center + 75, textY: size - 85, planetsX: center + 60, planetsY: size - 55 },
    // House 7 - Bottom center (opposite to Lagna)
    { house: 7, points: `${center},${size - 10} ${center - outerSize},${center} ${center},${center} ${center + outerSize},${center}`, textX: center, textY: size - 50, planetsX: center - 20, planetsY: size - 25 },
  ];

  return (
    <svg viewBox="0 0 300 300" className="kundali-chart-svg north-indian">
      {/* Outer border */}
      <rect x="10" y="10" width={size - 20} height={size - 20} fill="transparent" stroke="var(--accent-gold)" strokeWidth="2" />

      {/* Diagonal lines forming the diamond */}
      <line x1="10" y1="10" x2={size - 10} y2={size - 10} stroke="var(--accent-gold)" strokeWidth="1.5" />
      <line x1={size - 10} y1="10" x2="10" y2={size - 10} stroke="var(--accent-gold)" strokeWidth="1.5" />

      {/* Inner diamond */}
      <polygon
        points={`${center - innerSize},${center - innerSize} ${center + innerSize},${center - innerSize} ${center + innerSize},${center + innerSize} ${center - innerSize},${center + innerSize}`}
        fill="transparent"
        stroke="var(--accent-gold)"
        strokeWidth="1.5"
      />

      {/* House polygons with signs and planets */}
      {housePolygons.map(({ house, points, textX, textY, planetsX, planetsY }) => {
        const sign = getSignForHouse(house);
        const planets = chartData[house] || [];
        const isLagna = house === 1;

        return (
          <g key={house}>
            {/* Highlight Lagna house */}
            {isLagna && (
              <polygon
                points={points}
                fill="rgba(255, 215, 0, 0.15)"
                stroke="none"
              />
            )}

            {/* House number and sign */}
            <text
              x={textX}
              y={textY}
              textAnchor="middle"
              fill={isLagna ? 'var(--accent-gold)' : 'var(--text-secondary)'}
              fontSize="9"
              fontWeight={isLagna ? 'bold' : 'normal'}
            >
              {house === 1 ? `${signAbbr[sign]} (${ascText})` : signAbbr[sign]}
            </text>

            {/* Planets */}
            {planets.map((planet, idx) => (
              <text
                key={`${house}-${planet.abbr}`}
                x={planetsX + (idx % 2) * 25}
                y={planetsY + Math.floor(idx / 2) * 14}
                textAnchor="middle"
                fill={PLANET_COLORS[planet.abbr] || 'var(--text-primary)'}
                fontSize="11"
                fontWeight="bold"
              >
                {planetAbbr ? planetAbbr[planet.abbr] : planet.abbr}
                {planet.retrograde && <tspan fill="#FF0000" fontSize="8">℞</tspan>}
              </text>
            ))}
          </g>
        );
      })}

      {/* Center info */}
      <rect
        x={center - innerSize}
        y={center - innerSize}
        width={innerSize * 2}
        height={innerSize * 2}
        fill="rgba(0,0,0,0.3)"
        stroke="none"
      />
      <text x={center} y={center - 5} textAnchor="middle" fill="var(--accent-gold)" fontSize="12" fontWeight="bold">
        {title.includes('Navamsa') || title.includes('నవాంశ') ? 'D9' : 'D1'}
      </text>
      <text x={center} y={center + 12} textAnchor="middle" fill="var(--text-secondary)" fontSize="10">
        {title.includes('Navamsa') || title.includes('నవాంశ') ? (language === 'te' ? 'నవాంశ' : 'Navamsa') : (language === 'te' ? 'రాశి' : 'Rashi')}
      </text>
    </svg>
  );
};

/**
 * Unified Kundali Chart Component with Style Toggle
 */
export const KundaliChart = ({ chartData, title, ascendantSign, chartStyle = 'south' }) => {
  const { language } = useLanguage();

  return (
    <div className="kundali-chart-wrapper">
      <h4 style={{ textAlign: 'center', color: 'var(--accent-gold)', marginBottom: '0.5rem' }}>
        {title}
      </h4>
      {chartStyle === 'south' ? (
        <SouthIndianChart chartData={chartData} title={title} ascendantSign={ascendantSign} language={language} />
      ) : (
        <NorthIndianChart chartData={chartData} title={title} ascendantSign={ascendantSign} language={language} />
      )}
    </div>
  );
};

export default KundaliChart;
